import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { isLocalDev } from "@/lib/is-local-dev";

interface HeroProps {
  headline: string;
  subheadline: string;
  primaryCTA: { text: string };
  secondaryCTA?: { text: string; onClick?: () => void };
  trustIndicator?: string;
}

export function Hero({
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  trustIndicator,
}: HeroProps) {
  const heroRef = React.useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -rect.top / rect.height));
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const opacity = 1 - scrollProgress * 1.5;
  const scale = 1 - scrollProgress * 0.05;
  const translateY = scrollProgress * 100;

  return (
    <section
      ref={heroRef}
      className="relative min-h-[100vh] flex items-center justify-center pt-20 lg:pt-24 pb-16 lg:pb-24 overflow-hidden"
    >
      <div
        className="container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        style={{
          opacity: Math.max(0, opacity),
          transform: `scale(${scale}) translateY(${translateY}px)`,
          transition: "transform 0.1s linear",
        }}
      >
        {/* Trust indicator badge */}
        {trustIndicator && (
          <div className="animate-fade-up stagger-1 opacity-0 mb-8">
            <span
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-full",
                "bg-muted border border-border",
                "text-sm text-muted-foreground"
              )}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              {trustIndicator}
            </span>
          </div>
        )}

        {/* Headline */}
        <h1
          className={cn(
            "animate-fade-up stagger-2 opacity-0",
            "text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl",
            "font-semibold tracking-display leading-[1.1]",
            "max-w-5xl mx-auto mb-6 lg:mb-8",
            "text-foreground"
          )}
        >
          {headline}
        </h1>

        {/* Subheadline */}
        <p
          className={cn(
            "animate-fade-up stagger-3 opacity-0",
            "text-base sm:text-lg lg:text-xl",
            "text-muted-foreground leading-relaxed",
            "max-w-2xl mx-auto mb-8 lg:mb-10"
          )}
        >
          {subheadline}
        </p>

        {/* CTAs */}
        <div
          className={cn(
            "animate-fade-up stagger-4 opacity-0",
            "flex flex-col sm:flex-row items-center justify-center gap-4"
          )}
        >
          <Link to={isLocalDev ? "/signup" : "/login"}>
            <Button
              size="lg"
              className="h-12 px-8 text-base rounded-full"
            >
              {primaryCTA.text}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>

          {secondaryCTA && (
            <Button
              variant="ghost"
              size="lg"
              onClick={secondaryCTA.onClick}
              className="h-12 px-8 text-base rounded-full text-foreground hover:text-foreground hover:bg-muted"
            >
              <Play className="w-4 h-4 mr-2" />
              {secondaryCTA.text}
            </Button>
          )}
        </div>

        {/* Decorative line */}
        <div
          className={cn(
            "animate-fade-up stagger-5 opacity-0",
            "mt-16 lg:mt-24 relative"
          )}
        >
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={cn(
          "absolute bottom-8 left-1/2 -translate-x-1/2",
          "animate-bounce opacity-50"
        )}
        style={{ opacity: Math.max(0, 0.5 - scrollProgress * 2) }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-border flex items-start justify-center p-2">
          <div className="w-1 h-2 rounded-full bg-muted-foreground animate-pulse" />
        </div>
      </div>
    </section>
  );
}
